import React, { useState, useCallback } from 'react';
import DraggableCard from './TiptapExtensions/DraggableCard';
import ReactDOM from 'react-dom';

// Типы для внутреннего использования компонентом
interface KanbanCard {
  id: string;
  title: string;
  description?: string;
  priority?: 'low' | 'medium' | 'high';
  deadline?: string;
  tags?: string[];
}

interface KanbanColumn {
  id: string;
  title: string;
  cardIds: string[];
  isCompletedColumn?: boolean;
}

export interface BoardState {
  columns: KanbanColumn[];
  cards: { [key: string]: KanbanCard };
  columnOrder: string[];
  boardTitle: string;
}

interface KanbanBoardProps {
    boardState: BoardState;
    onBoardChange: (boardId: string, newState: BoardState) => void;
    boardId: string;
    itemId: string;
    onSetCompletedColumn: (boardId: string, columnId: string) => void;
    onDeleteColumn: (boardId: string, columnId: string) => void;
    onAddColumn: (boardId: string) => void;
    onDeleteBoard?: (boardId: string) => void;
    canDeleteBoard?: boolean;
}

const KanbanBoardStandalone: React.FC<KanbanBoardProps> = ({ 
    boardState, 
    onBoardChange,
    boardId,
    itemId,
    onDeleteBoard,
    canDeleteBoard = false
}) => {
    const [filterText] = useState('');
    const [filterPriority] = useState<string | null>(null);
    const [draggedCardId, setDraggedCardId] = useState<string | null>(null);
    const [draggedFromColumnId, setDraggedFromColumnId] = useState<string | null>(null);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [columnToDelete, setColumnToDelete] = useState<KanbanColumn | null>(null);
    const [isHoveringBoard, setIsHoveringBoard] = useState(false);
    const [deleteBoardModalVisible, setDeleteBoardModalVisible] = useState(false);

    const handleStateChange = (newState: BoardState) => {
        onBoardChange(boardId, newState);
    };

    const handleBoardTitleChange = (newTitle: string) => {
        const updatedState = { ...boardState, boardTitle: newTitle };
        handleStateChange(updatedState);
    };

    const handleCardTitleChange = (cardId: string, newTitle: string) => {
        const updatedState = JSON.parse(JSON.stringify(boardState));
        if (updatedState.cards[cardId]) {
            updatedState.cards[cardId].title = newTitle;
            handleStateChange(updatedState);
        }
    };

    const handleCardDescriptionChange = (cardId: string, newDescription: string) => {
        const updatedState = JSON.parse(JSON.stringify(boardState));
        if (updatedState.cards[cardId]) {
            updatedState.cards[cardId].description = newDescription;
            handleStateChange(updatedState);
        }
    };

    const handleCardPriorityChange = (cardId: string, newPriority: 'low' | 'medium' | 'high' | undefined) => {
        const updatedState = JSON.parse(JSON.stringify(boardState));
        if (updatedState.cards[cardId]) {
            updatedState.cards[cardId].priority = newPriority;
            handleStateChange(updatedState);
        }
    };

    const handleDeadlineChange = (cardId: string, newDeadline: string) => {
        const updatedState = JSON.parse(JSON.stringify(boardState));
        if (updatedState.cards[cardId]) {
            updatedState.cards[cardId].deadline = newDeadline;
            handleStateChange(updatedState);
        }
    };
    
    const handleColumnTitleChange = (columnId: string, newTitle: string) => {
        const columnIndex = boardState.columns.findIndex(col => col.id === columnId);
        if (columnIndex === -1) return;

        const updatedState = JSON.parse(JSON.stringify(boardState));
        updatedState.columns[columnIndex].title = newTitle;
        handleStateChange(updatedState);
    };

    const handleAddCard = (columnId: string) => {
        const newCardId = `card-id-${Math.random().toString(16).slice(2)}`;
        const newCard: KanbanCard = {
            id: newCardId,
            title: '',
            description: '',
            priority: 'medium',
        };

        const updatedState = JSON.parse(JSON.stringify(boardState));
        updatedState.cards[newCardId] = newCard;
        const targetColumnIndex = updatedState.columns.findIndex((col: KanbanColumn) => col.id === columnId);
        if (targetColumnIndex !== -1) {
            updatedState.columns[targetColumnIndex].cardIds.push(newCardId);
            handleStateChange(updatedState);
        }
    };

    const handleDeleteCard = (columnId: string, cardId: string) => {
        const updatedState = JSON.parse(JSON.stringify(boardState));
        if (updatedState.cards[cardId]) {
            delete updatedState.cards[cardId];
        }
        const targetColumnIndex = updatedState.columns.findIndex((col: KanbanColumn) => col.id === columnId);
        if (targetColumnIndex !== -1) {
            updatedState.columns[targetColumnIndex].cardIds = updatedState.columns[targetColumnIndex].cardIds.filter((id: string) => id !== cardId);
        }
        handleStateChange(updatedState);
    };

    const handleMoveToNextColumn = (cardId: string, currentColumnId: string) => {
        const currentColumnIndex = boardState.columnOrder.indexOf(currentColumnId);
        if (currentColumnIndex === -1) return;
        
        const nextColumnIndex = currentColumnIndex + 1;
        if (nextColumnIndex >= boardState.columnOrder.length) return;
        
        const nextColumnId = boardState.columnOrder[nextColumnIndex];
        
        // Создаем обновленное состояние
        const updatedState = JSON.parse(JSON.stringify(boardState));
        
        // Удаляем карточку из текущего столбца
        const currentColumn = updatedState.columns.find((col: KanbanColumn) => col.id === currentColumnId);
        if (currentColumn) {
            currentColumn.cardIds = currentColumn.cardIds.filter((id: string) => id !== cardId);
        }
        
        // Добавляем карточку в следующий столбец
        const nextColumn = updatedState.columns.find((col: KanbanColumn) => col.id === nextColumnId);
        if (nextColumn) {
            nextColumn.cardIds.push(cardId);
        }
        
        handleStateChange(updatedState);
    };

    const handleDragStart = (cardId: string, sourceColumnId: string) => {
        setDraggedCardId(cardId);
        setDraggedFromColumnId(sourceColumnId);
    };

    const handleDrop = (destinationColumnId: string) => {
        if (!draggedCardId || !draggedFromColumnId || draggedFromColumnId === destinationColumnId) {
            setDraggedCardId(null);
            setDraggedFromColumnId(null);
            return;
        }

        const updatedState = JSON.parse(JSON.stringify(boardState));
        const sourceColumn = updatedState.columns.find((col: KanbanColumn) => col.id === draggedFromColumnId);
        const destinationColumn = updatedState.columns.find((col: KanbanColumn) => col.id === destinationColumnId);

        if (sourceColumn && destinationColumn) {
            sourceColumn.cardIds = sourceColumn.cardIds.filter((id: string) => id !== draggedCardId);
            destinationColumn.cardIds.push(draggedCardId);
            handleStateChange(updatedState);
        }

        setDraggedCardId(null);
        setDraggedFromColumnId(null);
    };
    
    const handleAddColumn = () => {
        const newColumnId = `col-${Math.random().toString(36).substr(2, 9)}`;
        const updatedState = {
          ...boardState,
          columns: [
            ...boardState.columns,
            { id: newColumnId, title: 'Новая колонка', cardIds: [] }
          ],
          columnOrder: [...boardState.columnOrder, newColumnId],
        };
        handleStateChange(updatedState);
    };

    const confirmDeleteColumn = (columnId: string) => {
        const column = boardState.columns.find(col => col.id === columnId);
        if (column) {
            setColumnToDelete(column);
            setDeleteModalVisible(true);
        }
    };
    
    const cancelDeleteColumn = () => {
        setDeleteModalVisible(false);
        setColumnToDelete(null);
    };

    const handleConfirmDeleteColumn = () => {
        if (!columnToDelete) return;
        const columnId = columnToDelete.id;

        if (boardState.columnOrder.length <= 1) return;
        
        const cardIdsToRemove = columnToDelete.cardIds || [];
        const newCards = { ...boardState.cards };
        cardIdsToRemove.forEach((cardId: string) => {
            delete newCards[cardId];
        });

        const updatedState = {
            ...boardState,
            columns: boardState.columns.filter(col => col.id !== columnId),
            columnOrder: boardState.columnOrder.filter(id => id !== columnId),
            cards: newCards
        };
        
        handleStateChange(updatedState);
        setDeleteModalVisible(false);
        setColumnToDelete(null);
    };

    const handleSetAsCompletedColumn = (columnId: string) => {
        const updatedColumns = boardState.columns.map(col => ({
            ...col,
            isCompletedColumn: col.id === columnId
        }));
        
        const updatedState = { ...boardState, columns: updatedColumns };
        handleStateChange(updatedState);
    };

    const calculateColumnStats = useCallback(() => {
        if (!boardState || !boardState.cards || !boardState.columns) {
            return { columnStats: [], totalTasks: 0, completionPercentage: 0 };
        }
        
        const total = Object.keys(boardState.cards).length;
        const completedColumn = boardState.columns.find(col => col.isCompletedColumn);
        const completed = completedColumn?.cardIds?.length || 0;
        const completionPercentage = total > 0 ? Math.round((completed / total) * 100) : 0;
        
        const columnStats = boardState.columns.map((column: KanbanColumn) => ({
            id: column.id,
            title: column.title,
            count: column.cardIds?.length || 0
        }));
        
        return { columnStats, totalTasks: total, completionPercentage };
    }, [boardState]);

    const { columnStats, totalTasks, completionPercentage } = calculateColumnStats();

    const filteredCards = (columnId: string, cardIds: string[]): KanbanCard[] => {
        if (!boardState || !boardState.cards) return [];
        const cardsInColumn = (cardIds || []).map(id => boardState.cards[id]).filter((card): card is KanbanCard => card !== undefined);
        return cardsInColumn.filter(card => {
            const titleMatch = filterText ? card.title.toLowerCase().includes(filterText.toLowerCase()) : true;
            const priorityMatch = filterPriority ? card.priority === filterPriority : true;
            return titleMatch && priorityMatch;
        });
    };

    const confirmDeleteBoard = () => {
        setDeleteBoardModalVisible(true);
    };

    const cancelDeleteBoard = () => {
        setDeleteBoardModalVisible(false);
    };

    const handleConfirmDeleteBoard = () => {
        console.log('Confirming delete board:', boardId);
        onDeleteBoard?.(boardId);
        setDeleteBoardModalVisible(false);
    };

    if (!boardState) {
        return <div>Загрузка...</div>;
    }

    return (
        <div 
            className="kanban-board-react-content p-4 rounded-lg border border-gray-200 dark:border-gray-700 relative group"
            onMouseEnter={() => setIsHoveringBoard(true)}
            onMouseLeave={() => setIsHoveringBoard(false)}
        >
            {/* Заголовок доски с кнопкой удаления */}
            <div className="relative mb-4 flex items-center">
                <input
                    type="text"
                    value={boardState.boardTitle}
                    onChange={(e) => handleBoardTitleChange(e.target.value)}
                    className="text-xl font-bold text-gray-900 dark:text-white bg-transparent border-none outline-none flex-1 text-center"
                    placeholder="Название доски"
                />
                {canDeleteBoard && (
                    <button
                        onClick={confirmDeleteBoard}
                        className="ml-2 p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-all duration-200 flex-shrink-0"
                        title="Удалить доску"
                        style={{ minWidth: '36px', minHeight: '36px' }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H8a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                    </button>
                )}
            </div>

            {/* Статистика */}
            <div className="mb-4">
                <div className="p-3 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm space-y-3">
                    <div className="flex gap-2 flex-wrap">
                        <div className="stat-item text-xs bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300 py-1 px-2 rounded">
                            <span className="font-medium">Всего задач:</span> {totalTasks}
                        </div>
                        {columnStats.map((column, index) => (
                            <div 
                                key={column.id} 
                                className={`stat-item text-xs py-1 px-2 rounded ${
                                    index % 4 === 0 ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300' :
                                    index % 4 === 1 ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
                                    index % 4 === 2 ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' :
                                    'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300'
                                }`}
                            >
                                <span className="font-medium">{column.title}:</span> {column.count}
                            </div>
                        ))}
                    </div>
                    
                    <div className="w-full sm:w-auto sm:flex sm:items-center">
                        <div className="flex items-center justify-between mb-1 sm:mb-0 sm:mr-2">
                            <span className="text-xs text-gray-500 dark:text-gray-400 sm:mr-2">Прогресс выполнения</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 sm:hidden">{completionPercentage}%</span>
                        </div>
                        <div className="w-full sm:w-24 bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden sm:mr-2">
                            <div className="bg-green-500 h-full rounded-full transition-all duration-300" style={{ width: `${completionPercentage}%` }}></div>
                        </div>
                        <span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">{completionPercentage}%</span>
                    </div>
                </div>
            </div>
            
            <div className="kanban-columns-container overflow-x-auto pb-2">
                <div className="inline-flex gap-4 p-4 w-max min-w-full">
                    {boardState.columnOrder.map(columnId => {
                        const column = boardState.columns.find(c => c.id === columnId);
                        if (!column) return null;

                        const cards = filteredCards(columnId, column.cardIds);
                        const isCompletedColumn = column.isCompletedColumn || false;

                        return (
                            <div
                                key={column.id}
                                className="kanban-column rounded-lg p-3 border border-gray-200 dark:border-gray-700 min-w-[250px] flex-shrink-0 w-[300px]"
                                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleDrop(column.id); }}
                            >
                                <div className="kanban-column-header flex items-center mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                                    <input
                                        type="text"
                                        value={column.title}
                                        onChange={(e) => handleColumnTitleChange(column.id, e.target.value)}
                                        className="text-sm font-medium text-gray-700 dark:text-gray-300 bg-transparent border-none p-0 focus:ring-0 mr-auto"
                                    />
                                    <div className="flex items-center">
                                        <button
                                            className="flex items-center justify-center text-blue-500 hover:text-blue-600 transition-colors mx-1 w-6 h-6"
                                            onClick={() => handleAddCard(column.id)}
                                            title="Добавить задачу"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                            </svg>
                                        </button>
                                        
                                        <button
                                            className={`flex items-center justify-center transition-colors mx-1 rounded ${isCompletedColumn ? 'text-green-500 hover:text-green-600 bg-green-50 dark:bg-green-900/20' : 'text-gray-400 hover:text-green-500'}`}
                                            onClick={() => handleSetAsCompletedColumn(column.id)}
                                            title={isCompletedColumn ? "Эта колонка используется для расчета % завершения" : "Отметить как колонку завершения"}
                                            style={{ width: '24px', height: '24px' }}
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-17l6 4 6-4 6 4v12l-6-4-6 4" />
                                            </svg>
                                        </button>
                                        
                                        {boardState.columnOrder.length > 1 && (
                                            <button
                                                className="flex items-center justify-center text-gray-400 hover:text-red-500 transition-colors mx-1 w-6 h-6"
                                                onClick={() => confirmDeleteColumn(column.id)}
                                                title="Удалить колонку"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className="min-h-[100px] kanban-cards-container pt-2">
                                    {cards.map((card, cardIndex) => (
                                        <DraggableCard
                                            key={card.id}
                                            card={card}
                                            columnId={column.id}
                                            handleDeleteCard={handleDeleteCard}
                                            handleCardTitleChange={handleCardTitleChange}
                                            handleCardDescriptionChange={handleCardDescriptionChange}
                                            handleCardPriorityChange={handleCardPriorityChange}
                                            handleDeadlineChange={handleDeadlineChange}
                                            handleDragStart={handleDragStart}
                                            isLastCard={cardIndex === cards.length - 1}
                                            onMoveToNextColumn={handleMoveToNextColumn}
                                            hasNextColumn={boardState.columnOrder.indexOf(column.id) < boardState.columnOrder.length - 1}
                                        />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                    
                    <div className="flex items-center justify-center">
                        <button
                            className="flex items-center justify-center text-blue-500 hover:text-blue-600 transition-colors w-16 h-24 mx-2"
                            onClick={handleAddColumn}
                            title="Добавить колонку"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            {deleteModalVisible && columnToDelete && ReactDOM.createPortal(
                <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-md w-full p-6 border border-gray-200 dark:border-gray-700">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Удаление столбца</h3>
                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                            Вы уверены, что хотите удалить столбец "{columnToDelete.title}"? Все задачи внутри этого столбца будут также удалены.
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 dark:text-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-md transition-colors"
                                onClick={cancelDeleteColumn}
                            >
                                Отмена
                            </button>
                            <button
                                className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-md transition-colors"
                                onClick={handleConfirmDeleteColumn}
                            >
                                Удалить
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {deleteBoardModalVisible && ReactDOM.createPortal(
                <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-md w-full p-6 border border-gray-200 dark:border-gray-700">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Удаление доски</h3>
                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                            Вы уверены, что хотите удалить доску "{boardState.boardTitle}"? Все задачи и колонки внутри этой доски будут также удалены.
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 dark:text-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-md transition-colors"
                                onClick={cancelDeleteBoard}
                            >
                                Отмена
                            </button>
                            <button
                                className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-md transition-colors"
                                onClick={handleConfirmDeleteBoard}
                            >
                                Удалить
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default KanbanBoardStandalone;